import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/* Founder Portal → /admin/academy-ethics
   Bilingual (EN/NL) CMS for the Stoic Ethics course: lessons, phases,
   course meta, terms, Chamber. Every save writes a revision snapshot and
   marks the course founder_edited so reseeding never overwrites. */

const TABS = ["Lessons", "Phases", "Course", "Terms", "Chamber", "History"];
const LANGS = ["en", "nl"];
const asLoc = (v) => (v && typeof v === "object" ? { en: v.en || "", nl: v.nl || "" } : { en: v || "", nl: "" });

function LangTabs({ lang, setLang }) {
  return (
    <div className="inline-flex border border-white/15" role="group" aria-label="Editing language">
      {LANGS.map((l) => (
        <button key={l} type="button" data-testid={`cms-lang-${l}`} aria-pressed={lang === l}
          onClick={() => setLang(l)}
          className={`px-4 py-2 min-h-[36px] text-[11px] tracking-[0.2em] uppercase ${lang === l ? "bg-[#2e5040] text-[#eaf5ee]" : "text-bone/50 hover:text-bone"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Text({ label, value, onChange, rows = 2, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.2em] uppercase text-bone/50 mb-1.5">{label}</label>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/30 border border-white/15 text-bone text-[13px] font-reading p-3 focus:outline-none focus:border-gold/60" />
    </div>
  );
}

function SaveBar({ onSave, saving, err, ok }) {
  return (
    <div className="flex items-center gap-4 pt-2">
      <button data-testid="cms-save" onClick={onSave} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-40">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save both languages
      </button>
      {ok && <span className="text-emerald-300 text-xs" data-testid="cms-save-ok">Saved</span>}
      {err && <span className="text-red-300 text-xs whitespace-pre-line" data-testid="cms-save-error">{err}</span>}
    </div>
  );
}

function useSaver(fn, reload) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const save = async (...args) => {
    setSaving(true); setErr(""); setOk(false);
    try {
      await fn(...args);
      setOk(true); setTimeout(() => setOk(false), 2500);
      reload && reload();
    } catch (e) {
      const d = e.detail;
      setErr(d && typeof d === "object" && d.problems ? `${d.message}:\n${d.problems.join("\n")}` : e.message || "Save failed");
    } finally { setSaving(false); }
  };
  return { save, saving, err, ok };
}

/* Generic bilingual editor over a set of localized fields. */
function LocEditor({ fields, source, onSave, saver, extra = null, testidPrefix = "cms" }) {
  const [lang, setLang] = useState("en");
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(Object.fromEntries(fields.map(([k]) => [k, asLoc(source[k])]))); }, [source]); // eslint-disable-line
  if (!form) return null;
  return (
    <div className="max-w-2xl space-y-4">
      <LangTabs lang={lang} setLang={setLang} />
      {fields.map(([k, label, rows]) => (
        <Text key={k} label={`${label} · ${lang.toUpperCase()}`} rows={rows || 2}
          testid={`${testidPrefix}-${k}`}
          value={form[k][lang]}
          onChange={(v) => setForm((x) => ({ ...x, [k]: { ...x[k], [lang]: v } }))} />
      ))}
      {extra}
      <SaveBar onSave={() => onSave(form)} saving={saver.saving} err={saver.err} ok={saver.ok} />
    </div>
  );
}

const LESSON_FIELDS = [
  ["title", "Title", 1], ["subtitle", "Subtitle / threshold", 2],
  ["historicalCore", "Historical core", 4], ["modernTranslation", "Modern translation", 3],
  ["scenario", "Scenario", 3], ["mentorIntervention", "Mentor intervention", 2],
  ["journalPrompt", "Journal prompt", 2], ["masteryEvidence", "Mastery evidence", 2],
];

function LessonEditor({ course, reload }) {
  const [phaseSlug, setPhaseSlug] = useState(course.phases[0].slug);
  const phase = course.phases.find((p) => p.slug === phaseSlug) || course.phases[0];
  const [lessonSlug, setLessonSlug] = useState(phase.lessons[0].slug);
  const lesson = phase.lessons.find((l) => l.slug === lessonSlug) || phase.lessons[0];
  const [mentor, setMentor] = useState(lesson.mentor);
  const [status, setStatus] = useState(lesson.publicationStatus || "published");
  const saver = useSaver((payload) => adminApi.putEthicsLesson(phase.slug, lesson.slug, payload), reload);
  useEffect(() => { setMentor(lesson.mentor); setStatus(lesson.publicationStatus || "published"); }, [lessonSlug, phaseSlug, course]); // eslint-disable-line

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <select value={phaseSlug} data-testid="cms-phase-select"
          onChange={(e) => { setPhaseSlug(e.target.value); setLessonSlug(course.phases.find((p) => p.slug === e.target.value).lessons[0].slug); }}
          className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
          {course.phases.map((p) => <option key={p.slug} value={p.slug}>Phase {p.order}: {asLoc(p.title).en}</option>)}
        </select>
        <select value={lessonSlug} data-testid="cms-lesson-select" onChange={(e) => setLessonSlug(e.target.value)}
          className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
          {phase.lessons.map((l) => <option key={l.slug} value={l.slug}>Lesson {l.order}: {asLoc(l.title).en}</option>)}
        </select>
        <select value={status} data-testid="cms-lesson-status" onChange={(e) => setStatus(e.target.value)}
          className="bg-black/30 border border-gold/40 text-gold text-[13px] px-3 py-2">
          {["draft", "review", "published"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={mentor} onChange={(e) => setMentor(e.target.value)} data-testid="cms-lesson-mentor"
          className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
          {["marcus", "epictetus", "seneca", "musonius", "hipparchia"].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <a href={`/academy/ethics/phase/${phase.slug}/lesson/${lesson.slug}`} target="_blank" rel="noreferrer"
          className="text-[11px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 px-3 py-2 hover:border-gold/40">View live</a>
      </div>
      <LocEditor key={`${phaseSlug}/${lessonSlug}`} fields={LESSON_FIELDS} source={lesson} saver={saver} testidPrefix="cms-lesson"
        onSave={(form) => saver.save({ ...form, mentor, publicationStatus: status })} />
    </div>
  );
}

function PhaseEditor({ course, reload }) {
  const [slug, setSlug] = useState(course.phases[0].slug);
  const phase = course.phases.find((p) => p.slug === slug);
  const saver = useSaver((payload) => adminApi.putEthicsPhase(slug, payload), reload);
  return (
    <div className="space-y-4">
      <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="cms-phases-select"
        className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
        {course.phases.map((p) => <option key={p.slug} value={p.slug}>Phase {p.order}: {asLoc(p.title).en}</option>)}
      </select>
      <LocEditor key={slug} source={phase} saver={saver} onSave={(form) => saver.save(form)} testidPrefix="cms-phase"
        fields={[["title", "Title", 1], ["movement", "Movement", 1], ["centralQuestion", "Central question", 2],
                 ["coreAbility", "Core ability", 2], ["introduction", "Introduction", 4]]} />
    </div>
  );
}

function CourseEditor({ course, reload }) {
  const saver = useSaver((payload) => adminApi.putEthicsCourse(payload), reload);
  return (
    <LocEditor source={course} saver={saver} onSave={(form) => saver.save(form)} testidPrefix="cms-course"
      fields={[["title", "Title", 1], ["subtitle", "Subtitle", 1], ["hallName", "Hall name", 1], ["theme", "Theme", 2],
               ["introduction", "Introduction", 4], ["heroIntro", "Hero intro", 2], ["thresholdLine", "Threshold line", 1],
               ["historicalNote", "Historical note", 4]]} />
  );
}

function TermsEditor({ terms, reload }) {
  const [id, setId] = useState(terms[0]?.id);
  const term = terms.find((t) => t.id === id) || terms[0];
  const saver = useSaver((payload) => adminApi.putEthicsTerm(term.id, payload), reload);
  if (!term) return null;
  return (
    <div className="space-y-4">
      <select value={id} onChange={(e) => setId(e.target.value)} data-testid="cms-terms-select"
        className="bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2">
        {terms.map((t) => <option key={t.id} value={t.id}>{t.transliteration}</option>)}
      </select>
      <LocEditor key={id} source={term} saver={saver} onSave={(form) => saver.save(form)} testidPrefix="cms-term"
        fields={[["plain", "Plain definition", 2], ["academic", "Historical status and context", 3],
                 ["application", "Practical meaning", 2], ["example", "Common misunderstanding", 2]]} />
    </div>
  );
}

function ChamberEditor({ chamber, reload }) {
  const saver = useSaver((payload) => adminApi.putEthicsChamber(payload), reload);
  return (
    <div className="space-y-4">
      <p className="text-xs text-fog/60 font-reading max-w-2xl">The stages, testimonies and rubric of the Chamber are structural; edit the framing texts here in both languages.</p>
      <LocEditor source={chamber} saver={saver} onSave={(form) => saver.save(form)} testidPrefix="cms-chamber"
        fields={[["title", "Case title", 1], ["intro", "Introduction", 4], ["pressure", "Pressure", 3],
                 ["closing", "Closing", 3], ["recognitionTitle", "Recognition title", 1], ["recognitionText", "Recognition text", 3]]} />
    </div>
  );
}

function History() {
  const [key, setKey] = useState("");
  const [revisions, setRevisions] = useState([]);
  const load = useCallback(() => { adminApi.getEthicsRevisions(key).then((d) => setRevisions(d.revisions || [])); }, [key]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="max-w-2xl space-y-4">
      <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="Filter by entity key, e.g. reality-before-preference/what-is-before-what-i-want"
        data-testid="cms-history-filter"
        className="w-full bg-black/30 border border-white/15 text-bone text-[13px] px-3 py-2 focus:outline-none focus:border-gold/60" />
      <div className="space-y-2" data-testid="cms-history-list">
        {revisions.length === 0 && <p className="text-fog/50 text-sm font-reading italic">No revisions recorded yet. Every save writes one.</p>}
        {revisions.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 border border-white/10 px-4 py-2.5 text-[13px]">
            <span className="text-bone/80">{r.entityType} · <span className="font-mono text-gold/80">{r.entityKey}</span></span>
            <span className="text-fog/50">{String(r.savedAt).slice(0, 19).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminEthicsCMS() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Lessons");
  const load = useCallback(() => { adminApi.getEthicsFull().then(setData).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const body = useMemo(() => {
    if (!data?.course) return null;
    if (tab === "Lessons") return <LessonEditor course={data.course} reload={load} />;
    if (tab === "Phases") return <PhaseEditor course={data.course} reload={load} />;
    if (tab === "Course") return <CourseEditor course={data.course} reload={load} />;
    if (tab === "Terms") return <TermsEditor terms={data.terms || []} reload={load} />;
    if (tab === "Chamber") return <ChamberEditor chamber={data.chamber} reload={load} />;
    return <History />;
  }, [data, tab, load]);

  return (
    <AdminShell title="Stoic Ethics · Course CMS">
      <div data-testid="admin-ethics-cms-page">
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`cms-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border ${tab === t ? "border-gold/60 text-gold bg-gold/5" : "border-white/10 text-bone/50 hover:border-gold/30"}`}>{t}</button>
          ))}
          <Link to="/admin/academy-ethics-media" className="ml-auto text-[11px] tracking-[0.2em] uppercase text-bone/50 border border-white/10 px-4 py-2 hover:border-gold/30">Media slots</Link>
        </div>
        {body || <p className="text-fog/50 text-sm">Loading course…</p>}
      </div>
    </AdminShell>
  );
}
